/**
 * Sector plant lists shared by the company-profile form and the operations-template agent.
 * Monthly utility numbers are not stored here. Those stay screening defaults in the agent.
 */
const PRESETS = [
  {
    id: 'textiles',
    label: 'Textiles and dyeing',
    aliases: ['textiles', 'textile', 'cotton_textiles', 'wool_silk_synthetic_fibre_textiles', 'jute_hemp_mesta_textiles', 'dyeing'],
    unitNames: ['Dyeing and finishing unit', 'Weaving shed', 'Garment floor'],
    products: ['Dyed fabric', 'Yarn', 'Finished cloth', 'Knitwear'],
    processes: ['Dyeing', 'Finishing', 'Weaving', 'Bleaching'],
    machinery: ['Boiler', 'Stenter', 'Jet dyeing machine', 'Jigger'],
    fuels: ['coal', 'lpg', 'electricity'],
    energySources: ['Coal boiler', 'Grid electricity', 'LPG'],
    materials: ['Grey fabric', 'Dyes', 'Textile chemicals', 'Yarn'],
    capacityUnits: ['metres', 'kg', 'pieces'],
    chemicals: ['Dyes', 'Bleaching agents', 'Fixing agents', 'Softeners'],
    waterTreatments: ['Primary treatment', 'Biological treatment', 'Color removal'],
  },
  {
    id: 'garments',
    label: 'Garments and stitching',
    aliases: ['hosiery_garments', 'garments', 'apparel'],
    unitNames: ['Stitching unit', 'Cutting floor'],
    products: ['Readymade garments', 'Knitwear', 'Uniforms'],
    processes: ['Cutting', 'Stitching', 'Finishing', 'Packing'],
    machinery: ['Sewing machine', 'Cutter', 'Ironing press', 'Overlock'],
    fuels: ['electricity'],
    energySources: ['Grid electricity'],
    materials: ['Fabric rolls', 'Thread', 'Buttons', 'Packing polybags'],
    capacityUnits: ['pieces', 'kg'],
    chemicals: ['Detergents', 'Stain removers'],
    waterTreatments: ['Screening', 'Reuse in utilities'],
  },
  {
    id: 'food_processing',
    label: 'Food processing',
    aliases: ['food_processing', 'food_products_industry', 'beverages_tobacco_products', 'food'],
    unitNames: ['Processing plant', 'Packing hall'],
    products: ['Packaged food', 'Bakery goods', 'Edible oil', 'Spices'],
    processes: ['Retort', 'Packing', 'Cooking', 'Pasteurization'],
    machinery: ['Retort', 'Cold room', 'Boiler', 'Filling line'],
    fuels: ['lpg', 'electricity', 'diesel'],
    energySources: ['LPG', 'Grid electricity', 'Diesel generator'],
    materials: ['Raw ingredients', 'Packaging film', 'Cleaning chemicals'],
    capacityUnits: ['kg', 'litres', 'tonnes'],
    chemicals: ['Cleaning agents (CIP)', 'Sanitizers', 'Food-grade preservatives'],
    waterTreatments: ['Screening', 'Biological treatment', 'Disinfection'],
  },
  {
    id: 'dairy',
    label: 'Dairy',
    aliases: ['dairy'],
    unitNames: ['Dairy plant', 'Chilling centre'],
    products: ['Pasteurized milk', 'Curd', 'Paneer'],
    processes: ['Pasteurization', 'Chilling', 'Packing'],
    machinery: ['Pasteurizer', 'Chiller', 'Homogenizer'],
    fuels: ['lpg', 'electricity'],
    energySources: ['Grid electricity', 'LPG'],
    materials: ['Raw milk', 'Culture', 'Packaging'],
    capacityUnits: ['litres', 'kg'],
    chemicals: ['Cleaning agents (CIP)', 'Boiler treatment chemicals'],
    waterTreatments: ['Screening', 'Biological treatment', 'Reuse in utilities'],
  },
  {
    id: 'rice_sugar',
    label: 'Rice and sugar milling',
    aliases: ['rice', 'sugar', 'sugar_mill'],
    unitNames: ['Rice mill', 'Sugar mill'],
    products: ['Milled rice', 'Sugar', 'Bran'],
    processes: ['Parboiling', 'Milling', 'Cane crushing'],
    machinery: ['Boiler', 'Huller', 'Cane mill'],
    fuels: ['biomass', 'electricity'],
    energySources: ['Biomass boiler', 'Grid electricity'],
    materials: ['Paddy', 'Sugarcane', 'Bags'],
    capacityUnits: ['tonnes', 'kg'],
    chemicals: ['Boiler treatment chemicals'],
    waterTreatments: ['Sedimentation', 'Reuse in utilities'],
  },
  {
    id: 'foundry',
    label: 'Foundry and basic metals',
    aliases: ['foundry', 'basic_metal_industries', 'metal'],
    unitNames: ['Foundry', 'Melting shop'],
    products: ['Castings', 'Ingots', 'Machined metal parts'],
    processes: ['Melting', 'Casting', 'Fettling'],
    machinery: ['Furnace', 'Cupola', 'Ladle'],
    fuels: ['coal', 'furnace oil', 'electricity'],
    energySources: ['Coal furnace', 'Furnace oil', 'Grid electricity'],
    materials: ['Scrap metal', 'Pig iron', 'Foundry sand', 'Flux'],
    capacityUnits: ['tonnes', 'kg'],
    chemicals: ['Flux', 'Binders', 'Coatings'],
    waterTreatments: ['Sedimentation', 'Sludge dewatering'],
  },
  {
    id: 'steel_fabrication',
    label: 'Steel fabrication',
    aliases: ['metal_products', 'fabrication', 'steel'],
    unitNames: ['Fabrication shop', 'Welding bay'],
    products: ['Steel structures', 'Fabricated parts', 'Gates and frames'],
    processes: ['Cutting', 'Welding', 'Painting'],
    machinery: ['Welding set', 'Cutting torch', 'Press brake'],
    fuels: ['lpg', 'electricity'],
    energySources: ['LPG', 'Grid electricity'],
    materials: ['Steel plate', 'Electrodes', 'Welding gas'],
    capacityUnits: ['tonnes', 'kg', 'pieces'],
    chemicals: ['Degreasers', 'Paints', 'Coolants'],
    waterTreatments: ['Oil-water separator', 'Sludge dewatering'],
  },
  {
    id: 'automotive',
    label: 'Automotive components',
    aliases: ['automotive', 'transport_equipment_parts', 'auto'],
    unitNames: ['Machine shop', 'Paint shop'],
    products: ['Auto components', 'Machined parts', 'Painted assemblies'],
    processes: ['Machining', 'Coating', 'Assembly'],
    machinery: ['CNC', 'Paint booth', 'Press'],
    fuels: ['diesel', 'electricity'],
    energySources: ['Grid electricity', 'Diesel generator'],
    materials: ['Steel bar', 'Paint', 'Coolant'],
    capacityUnits: ['pieces', 'kg'],
    chemicals: ['Coolants', 'Lubricants', 'Degreasers', 'Paints'],
    waterTreatments: ['Oil-water separator', 'Chemical coagulation'],
  },
  {
    id: 'plastics',
    label: 'Plastics and rubber',
    aliases: ['rubber_plastic_products', 'plastics', 'rubber', 'polymer'],
    unitNames: ['Moulding shop', 'Extrusion line'],
    products: ['Moulded plastic parts', 'Plastic packaging', 'Rubber components'],
    processes: ['Injection moulding', 'Extrusion', 'Vulcanizing'],
    machinery: ['Injection press', 'Chiller', 'Extruder', 'Vulcanizing press'],
    fuels: ['electricity'],
    energySources: ['Grid electricity'],
    materials: ['Polymer resin', 'Rubber compound', 'Masterbatch'],
    capacityUnits: ['kg', 'pieces'],
    chemicals: ['Masterbatch', 'Release agents'],
    waterTreatments: ['Cooling tower treatment', 'Filtration'],
  },
  {
    id: 'chemicals',
    label: 'Chemicals',
    aliases: ['chemical_chemical_products', 'chemicals', 'pharma', 'pharmaceuticals'],
    unitNames: ['Process plant', 'Formulation unit'],
    products: ['Specialty chemicals', 'Paints', 'Formulated products'],
    processes: ['Reaction', 'Formulation', 'Packing'],
    machinery: ['Reactor', 'Distillation column', 'HVAC'],
    fuels: ['furnace oil', 'lpg', 'electricity'],
    energySources: ['Furnace oil', 'LPG', 'Grid electricity'],
    materials: ['Solvents', 'Acids', 'Process chemicals'],
    capacityUnits: ['kg', 'litres', 'tonnes'],
    chemicals: ['Solvents', 'Acids', 'Process additives'],
    waterTreatments: ['Neutralization', 'Biological treatment', 'Reuse in process'],
  },
  {
    id: 'cement_minerals',
    label: 'Cement, ceramics, and minerals',
    aliases: ['non_metallic_mineral_products', 'cement', 'ceramics', 'coir_ceramic_glass_products', 'brick'],
    unitNames: ['Grinding unit', 'Kiln shed', 'Tile plant'],
    products: ['Cement products', 'Bricks', 'Ceramic tiles', 'Glassware'],
    processes: ['Clinker grinding', 'Kiln firing', 'Crushing'],
    machinery: ['Ball mill', 'Kiln', 'Jaw crusher'],
    fuels: ['coal', 'lpg', 'diesel'],
    energySources: ['Coal', 'LPG kiln', 'Diesel generator'],
    materials: ['Clinker', 'Clay', 'Aggregate'],
    capacityUnits: ['tonnes', 'pieces'],
    chemicals: ['Grinding aids', 'Glazes'],
    waterTreatments: ['Sedimentation', 'Filtration'],
  },
  {
    id: 'paper_printing',
    label: 'Paper and printing',
    aliases: ['paper_products_printing', 'paper', 'printing'],
    unitNames: ['Printing press', 'Paper unit'],
    products: ['Printed packaging', 'Notebooks', 'Paper'],
    processes: ['Offset printing', 'Pulping', 'Binding'],
    machinery: ['Printing press', 'Dryer', 'Digester'],
    fuels: ['lpg', 'coal', 'electricity'],
    energySources: ['Grid electricity', 'LPG', 'Coal boiler'],
    materials: ['Paper reels', 'Ink', 'Pulp'],
    capacityUnits: ['kg', 'pieces', 'reams'],
    chemicals: ['Inks', 'Fountain solution', 'Cleaning solvents'],
    waterTreatments: ['Primary treatment', 'Biological treatment'],
  },
  {
    id: 'furniture_handicrafts',
    label: 'Furniture and handicrafts',
    aliases: ['wood_products_furniture', 'handicrafts_artisanal_products', 'handicrafts', 'furniture'],
    unitNames: ['Workshop', 'Craft unit'],
    products: ['Furniture', 'Handicrafts', 'Wood products'],
    processes: ['Joinery', 'Wood carving', 'Finishing'],
    machinery: ['Panel saw', 'Lathe', 'Edge bander'],
    fuels: ['electricity', 'diesel'],
    energySources: ['Grid electricity', 'Diesel'],
    materials: ['Plywood', 'Teak blanks', 'Polish'],
    capacityUnits: ['pieces', 'kg'],
    chemicals: ['Polish', 'Adhesives', 'Wood preservatives'],
    waterTreatments: ['Sedimentation', 'Filtration'],
  },
  {
    id: 'leather',
    label: 'Leather',
    aliases: ['leather_leather_products', 'leather'],
    unitNames: ['Tannery', 'Finishing unit'],
    products: ['Finished leather', 'Footwear', 'Leather goods'],
    processes: ['Tanning', 'Dyeing', 'Finishing'],
    machinery: ['Tanning drum', 'Dryer', 'Splitting machine'],
    fuels: ['coal', 'electricity'],
    energySources: ['Coal boiler', 'Grid electricity'],
    materials: ['Hides', 'Tanning chemicals', 'Dyes'],
    capacityUnits: ['kg', 'pieces', 'square feet'],
    chemicals: ['Tanning chemicals', 'Dyes', 'Finishing agents'],
    waterTreatments: ['Primary treatment', 'Biological treatment', 'Chrome recovery'],
  },
  {
    id: 'electronics',
    label: 'Electronics assembly',
    aliases: ['electronics', 'electrical_machinery_parts', 'machinery_parts_non_electrical'],
    unitNames: ['Assembly line', 'SMT floor'],
    products: ['Electronic assemblies', 'Motors', 'Wiring equipment'],
    processes: ['SMT assembly', 'Testing', 'Packing'],
    machinery: ['Reflow oven', 'Pick and place', 'Test bench'],
    fuels: ['electricity'],
    energySources: ['Grid electricity'],
    materials: ['PCBs', 'Components', 'Solder paste'],
    capacityUnits: ['pieces', 'kg'],
    chemicals: ['Flux', 'Solvents', 'Isopropyl alcohol'],
    waterTreatments: ['Neutralization', 'RO treatment'],
  },
  {
    id: 'logistics',
    label: 'Logistics and transport',
    aliases: ['logistics', 'transport_services', 'storage_warehousing', 'export_import', 'trading'],
    unitNames: ['Fleet yard', 'Warehouse', 'Export godown'],
    products: ['Line-haul freight', 'Warehousing', 'Export cargo'],
    processes: ['Line haul', 'Dispatch', 'Customs clearance', 'Fulfillment'],
    machinery: ['Truck', 'Forklift', 'Reach stacker', 'Delivery van'],
    fuels: ['diesel', 'cng', 'electricity'],
    energySources: ['Diesel', 'Grid electricity'],
    materials: ['Cartons', 'Pallets', 'Stretch wrap'],
    capacityUnits: ['tonne-km', 'shipments', 'kg'],
    chemicals: ['Degreasers', 'Coolants'],
    waterTreatments: ['Oil-water separator', 'Sedimentation'],
  },
  {
    id: 'cold_storage',
    label: 'Cold storage',
    aliases: ['cold_storage', 'cold storage'],
    unitNames: ['Cold store', 'Reefer yard'],
    products: ['Cold storage service', 'Frozen goods'],
    processes: ['Refrigeration', 'Loading'],
    machinery: ['Compressor', 'Reefer', 'Forklift'],
    fuels: ['electricity', 'diesel'],
    energySources: ['Grid electricity', 'Diesel generator'],
    materials: ['Refrigerant', 'Packaging'],
    capacityUnits: ['tonnes', 'pallet positions'],
    chemicals: ['Refrigerant oil', 'Cleaning agents'],
    waterTreatments: ['Defrost drain treatment', 'Filtration'],
  },
  {
    id: 'agriculture',
    label: 'Agriculture',
    aliases: ['agriculture', 'farming'],
    unitNames: ['Farm', 'Pack house'],
    products: ['Crops', 'Seeds', 'Fresh produce'],
    processes: ['Cultivation', 'Irrigation', 'Harvest'],
    machinery: ['Tractor', 'Pump', 'Harvester'],
    fuels: ['diesel', 'electricity'],
    energySources: ['Diesel', 'Grid electricity'],
    materials: ['Seeds', 'Fertilizer', 'Pesticides'],
    capacityUnits: ['tonnes', 'kg', 'hectares'],
    chemicals: ['Fertilizer', 'Pesticides'],
    waterTreatments: ['Settling pond', 'Reuse for irrigation'],
  },
  {
    id: 'construction',
    label: 'Construction',
    aliases: ['construction'],
    unitNames: ['Site office', 'Batching plant'],
    products: ['Civil works', 'Ready-mix concrete', 'Structures'],
    processes: ['Site work', 'Concreting', 'Finishing'],
    machinery: ['Mixer', 'DG set', 'Excavator'],
    fuels: ['diesel'],
    energySources: ['Diesel generator', 'Grid electricity'],
    materials: ['Cement', 'Steel', 'Aggregate'],
    capacityUnits: ['cubic metres', 'tonnes', 'square metres'],
    chemicals: ['Admixtures', 'Curing compounds'],
    waterTreatments: ['Sedimentation', 'Reuse for curing'],
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    aliases: ['healthcare'],
    unitNames: ['Clinic', 'Hospital block'],
    products: ['Clinical services', 'Diagnostics'],
    processes: ['Sterilization', 'Diagnostics', 'Laundry'],
    machinery: ['Autoclave', 'HVAC', 'Generator'],
    fuels: ['lpg', 'diesel', 'electricity'],
    energySources: ['Grid electricity', 'Diesel generator', 'LPG'],
    materials: ['Medical consumables', 'Disinfectants'],
    capacityUnits: ['patient-days', 'tests'],
    chemicals: ['Disinfectants', 'Detergents'],
    waterTreatments: ['Disinfection', 'Sewage treatment'],
  },
  {
    id: 'hospitality',
    label: 'Hotels and restaurants',
    aliases: ['tourism', 'hotels_restaurants', 'hospitality'],
    unitNames: ['Hotel', 'Kitchen'],
    products: ['Guest rooms', 'Meals', 'Banquets'],
    processes: ['Kitchen', 'Laundry', 'HVAC'],
    machinery: ['Kitchen range', 'Boiler', 'Laundry machine'],
    fuels: ['lpg', 'electricity', 'diesel'],
    energySources: ['LPG', 'Grid electricity', 'Diesel generator'],
    materials: ['Food ingredients', 'Linen', 'Cleaning chemicals'],
    capacityUnits: ['room-nights', 'covers'],
    chemicals: ['Detergents', 'Sanitizers'],
    waterTreatments: ['Grease trap', 'Sewage treatment'],
  },
  {
    id: 'retail_wholesale',
    label: 'Retail and wholesale',
    aliases: ['retail', 'retail_trade_services', 'wholesale', 'wholesale_trade_services', 'e_commerce'],
    unitNames: ['Store', 'Depot', 'Fulfillment centre'],
    products: ['Grocery', 'Consumer goods', 'Wholesale cartons'],
    processes: ['Checkout', 'Dispatch', 'Cold storage'],
    machinery: ['Refrigerator', 'Forklift', 'POS terminal'],
    fuels: ['electricity', 'diesel'],
    energySources: ['Grid electricity', 'Diesel'],
    materials: ['Grocery stock', 'Cartons', 'Plastic crates'],
    capacityUnits: ['kg', 'cases', 'orders'],
    chemicals: ['Cleaning agents', 'Refrigerant'],
    waterTreatments: ['Filtration', 'Sewage treatment'],
  },
  {
    id: 'office_services',
    label: 'Offices, consulting, and education',
    aliases: ['consulting', 'services', 'education', 'other', 'miscellaneous_manufacturing_industries'],
    unitNames: ['Office', 'Campus', 'Service centre'],
    products: ['Advisory services', 'Training', 'Campus operations'],
    processes: ['Office work', 'Campus operations', 'Advisory'],
    machinery: ['Air conditioner', 'Generator', 'Laptop'],
    fuels: ['electricity', 'diesel'],
    energySources: ['Grid electricity', 'Diesel generator'],
    materials: ['Stationery', 'IT equipment'],
    capacityUnits: ['people', 'service hours'],
    chemicals: ['Cleaning agents'],
    waterTreatments: ['Sewage treatment'],
  },
  {
    id: 'solar',
    label: 'Solar installation',
    aliases: ['solar'],
    unitNames: ['Site store', 'Installation crew'],
    products: ['Solar plants', 'Rooftop systems'],
    processes: ['Installation', 'Commissioning'],
    machinery: ['Inverter', 'DG set', 'Drill'],
    fuels: ['diesel', 'electricity'],
    energySources: ['Diesel generator', 'Grid electricity'],
    materials: ['Solar modules', 'Structures', 'Cables'],
    capacityUnits: ['kW', 'modules'],
    chemicals: ['Cleaning agents'],
    waterTreatments: ['Sedimentation'],
  },
  {
    id: 'data_center',
    label: 'Data center',
    aliases: ['data_center', 'data center'],
    unitNames: ['Server hall'],
    products: ['Hosting', 'Colocation'],
    processes: ['Server cooling', 'Power distribution'],
    machinery: ['Chiller', 'UPS', 'CRAH'],
    fuels: ['electricity', 'diesel'],
    energySources: ['Grid electricity', 'Diesel generator'],
    materials: ['Servers', 'Batteries'],
    capacityUnits: ['kW', 'racks'],
    chemicals: ['Water treatment chemicals'],
    waterTreatments: ['Cooling tower treatment', 'RO treatment'],
  },
];


const GENERAL = PRESETS.find((preset) => preset.id === 'office_services');

const normalize = (value) => String(value || '').toLowerCase().replace(/[_/]+/g, ' ').trim();

const resolveSectorFormPreset = (input) => {
  const haystack = [input && input.industrySector, input && input.businessDomain, input && input.industry]
    .map(normalize)
    .filter(Boolean)
    .join(' ');
  if (!haystack) return GENERAL;
  const match = PRESETS.find((preset) => preset.aliases.some((alias) => haystack.includes(normalize(alias))));
  return match || GENERAL;
};

const suggestEmptyFacilityFields = (preset, current) => ({
  ...current,
  unitName: String(current.unitName || '').trim() || (preset.unitNames[0] || ''),
  products: String(current.products || '').trim() || preset.products.slice(0, 3).join(', '),
  primaryEnergySource: String(current.primaryEnergySource || '').trim() || (preset.energySources[0] || ''),
  mainFuelsUsed: String(current.mainFuelsUsed || '').trim() || preset.fuels.slice(0, 2).join(', '),
  productionCapacityUnit: String(current.productionCapacityUnit || '').trim() || (preset.capacityUnits[0] || 'kg'),
});

const resolvePlantPresetFromMsme = (msmeData = {}) => {
  const manufacturingProfile = msmeData.manufacturingProfile || {};
  return resolveSectorFormPreset({
    businessDomain: msmeData.businessDomain,
    industry: msmeData.industry,
    industrySector: manufacturingProfile.industrySector || manufacturingProfile.beeSector
  });
};

const toPlantSetup = (preset) => ({
  id: preset.id,
  label: preset.label,
  unitNames: preset.unitNames,
  products: preset.products,
  processes: preset.processes,
  machinery: preset.machinery,
  fuels: preset.fuels,
  energySources: preset.energySources,
  materials: preset.materials,
  capacityUnits: preset.capacityUnits,
  chemicals: preset.chemicals,
  waterTreatments: preset.waterTreatments,
});

module.exports = {
  PRESETS,
  resolveSectorFormPreset,
  resolvePlantPresetFromMsme,
  suggestEmptyFacilityFields,
  toPlantSetup,
};
